import debounce from "lodash/debounce";
import { useCallback, useEffect } from "react";

const INTERSECTION_THRESHOLD = 5;
const LOAD_DELAY = 1000;

export const useLazyLoad = ({
  triggerRef,
  fetchNextPage = () => {},
  isFetching = false,
  hasNextPage = true,
  options = {},
}: {
  triggerRef: React.RefObject<HTMLElement>;
  fetchNextPage: () => void;
  isFetching: boolean;
  hasNextPage: boolean;
  options?: IntersectionObserverInit;
}) => {
  const _handleEntry = async (entry: IntersectionObserverEntry) => {
    const boundingRect = entry.boundingClientRect;
    const intersectionRect = entry.intersectionRect;
    if (hasNextPage && !isFetching) {
      if (
        entry.isIntersecting &&
        intersectionRect.bottom - boundingRect.bottom <= INTERSECTION_THRESHOLD
      ) {
        await fetchNextPage();
      }
    }
  };

  const handleEntry = debounce(_handleEntry, LOAD_DELAY);

  const onIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      handleEntry(entries[0]);
    },
    [handleEntry]
  );

  useEffect(() => {
    if (triggerRef?.current) {
      const container = triggerRef.current;
      const observer = new IntersectionObserver(onIntersect, options);

      observer.observe(container);

      return () => {
        observer.disconnect();
      };
    }
  }, [triggerRef, onIntersect, options]);
};
